// FILE: src/pages/AdminReferrals.jsx
import React, { useEffect, useState, useRef } from "react";
import { auth, db } from "../firebase";
import { collection, getDocs, doc, setDoc, updateDoc, query, where, serverTimestamp } from "firebase/firestore";
import SuperAdminNav from "../components/SuperAdminNav";

function csvEscape(v) {
  const s = String(v == null ? "" : v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export default function AdminReferrals() {
  const [me, setMe] = useState(() => auth.currentUser);
  const [codes, setCodes] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [toast, setToast] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", contactEmail: "" });
  const [saving, setSaving] = useState(false);
  const [selectedCode, setSelectedCode] = useState(null);
  const toastTimer = useRef(null);

  useEffect(() => auth.onAuthStateChanged((u) => setMe(u)), []);

  function showToast(msg) {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(""), 2500);
  }

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const codesSnap = await getDocs(collection(db, "referralCodes"));
      const codeRows = codesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setCodes(codeRows);

      const usersSnap = await getDocs(query(collection(db, "users"), where("refCode", "!=", null)));
      const userRows = usersSnap.docs.map(d => ({ uid: d.id, ...d.data() })).filter(u => u.refCode);
      setUsers(userRows);
    } catch (e) {
      setErr(e.message || "Failed to load");
    }
    setLoading(false);
  }

  useEffect(() => { if (me) load(); }, [me]);

  async function handleCreateCode(e) {
    e.preventDefault();
    if (!form.code.trim() || !form.name.trim()) return;
    setSaving(true);
    try {
      const code = form.code.trim().toUpperCase();
      await setDoc(doc(db, "referralCodes", code), {
        code,
        name: form.name.trim(),
        contactEmail: form.contactEmail.trim(),
        status: "active",
        createdAtMs: Date.now(),
      });
      setForm({ code: "", name: "", contactEmail: "" });
      setShowForm(false);
      showToast("Code created");
      await load();
    } catch (e) {
      setErr(e.message);
    }
    setSaving(false);
  }

  async function toggleStatus(code) {
    try {
      const next = code.status === "active" ? "inactive" : "active";
      await updateDoc(doc(db, "referralCodes", code.id), { status: next });
      showToast(`Code ${next === "active" ? "activated" : "deactivated"}`);
      await load();
    } catch (e) {
      setErr(e.message);
    }
  }

  function exportCSV() {
    const headers = ["Code", "Name", "Contact Email", "Signups", "Active Users", "Status"];
    const lines = [headers.join(",")];
    codes.forEach(c => {
      const codeUsers = users.filter(u => u.refCode === c.code);
      const active = codeUsers.filter(u => u.subscriptionStatus === "active").length;
      lines.push([c.code, c.name, c.contactEmail || "", codeUsers.length, active, c.status].map(csvEscape).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `referrals-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const selectedUsers = selectedCode ? users.filter(u => u.refCode === selectedCode) : [];

  return (
    <div className="ad ad-wrap">
      {toast && (
        <div style={{ position: "fixed", bottom: 24, right: 24, background: "#111", color: "#fff", padding: "10px 18px", borderRadius: 10, zIndex: 9999, fontSize: 14 }}>
          {toast}
        </div>
      )}

      <SuperAdminNav title="Admin" active="referrals" />

      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        {err && <div style={{ background: "#fff1f2", border: "1px solid #fecdd3", color: "#9f1239", borderRadius: 12, padding: 14, marginBottom: 16 }}>{err}</div>}

        {/* Header row */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
          <div>
            <h2 style={{ margin: "0 0 4px", color: "#650202" }}>Referral Codes</h2>
            <p style={{ margin: 0, color: "#657899", fontSize: 14 }}>Manage nursing home and partner referral codes. Track signups per code.</p>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={load} disabled={loading} style={{ padding: "9px 18px", borderRadius: 8, border: "1px solid #ddd", background: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
              {loading ? "Loading…" : "Refresh"}
            </button>
            <button onClick={exportCSV} disabled={codes.length === 0} style={{ padding: "9px 18px", borderRadius: 8, border: "none", background: "#003353", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
              Export CSV
            </button>
            <button onClick={() => setShowForm(true)} style={{ padding: "9px 18px", borderRadius: 8, border: "none", background: "#f15822", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
              + New Code
            </button>
          </div>
        </div>

        {/* New code form */}
        {showForm && (
          <div style={{ background: "#fff", border: "1px solid #e8ebf3", borderRadius: 16, padding: 24, marginBottom: 20 }}>
            <h3 style={{ margin: "0 0 16px", color: "#650202" }}>Create New Referral Code</h3>
            <form onSubmit={handleCreateCode} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 12, alignItems: "end" }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: "#657899", display: "block", marginBottom: 4 }}>Code *</label>
                <input value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="e.g. LWBCARE" maxLength={20} required style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #ddd", fontSize: 14 }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: "#657899", display: "block", marginBottom: 4 }}>Organisation Name *</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Sunnyside Nursing Home" required style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #ddd", fontSize: 14 }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: "#657899", display: "block", marginBottom: 4 }}>Contact Email</label>
                <input value={form.contactEmail} onChange={e => setForm(f => ({ ...f, contactEmail: e.target.value }))} placeholder="contact@nursinghome.ie" type="email" style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #ddd", fontSize: 14 }} />
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button type="submit" disabled={saving} style={{ padding: "9px 18px", borderRadius: 8, border: "none", background: "#980000", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                  {saving ? "Saving…" : "Create"}
                </button>
                <button type="button" onClick={() => setShowForm(false)} style={{ padding: "9px 18px", borderRadius: 8, border: "1px solid #ddd", background: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Codes table */}
        <div style={{ background: "#fff", border: "1px solid #e8ebf3", borderRadius: 16, overflow: "hidden", marginBottom: 24 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr style={{ background: "#f6f7fb" }}>
                {["Code", "Organisation", "Contact Email", "Signups", "Active Users", "Signup URL", "Status", "Actions"].map(h => (
                  <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700, color: "#650202", whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={{ padding: 32, textAlign: "center", color: "#657899" }}>Loading…</td></tr>
              ) : codes.length === 0 ? (
                <tr><td colSpan={8} style={{ padding: 32, textAlign: "center", color: "#657899" }}>No referral codes yet. Create one above.</td></tr>
              ) : codes.map((c, i) => {
                const codeUsers = users.filter(u => u.refCode === c.code);
                const active = codeUsers.filter(u => u.subscriptionStatus === "active").length;
                const signupUrl = `https://businessapp.lastwishbox.com/register?plan=free&ref=${c.code}`;
                return (
                  <tr key={c.id} style={{ borderTop: "1px solid #e8ebf3", background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                    <td style={{ padding: "12px 16px", fontWeight: 900, color: "#003353", letterSpacing: 1 }}>{c.code}</td>
                    <td style={{ padding: "12px 16px", fontWeight: 700, color: "#650202" }}>{c.name}</td>
                    <td style={{ padding: "12px 16px", color: "#657899" }}>{c.contactEmail || "—"}</td>
                    <td style={{ padding: "12px 16px" }}>
                      <button onClick={() => setSelectedCode(selectedCode === c.code ? null : c.code)} style={{ background: "none", border: "none", color: "#f15822", fontWeight: 700, fontSize: 14, cursor: "pointer", padding: 0 }}>
                        {codeUsers.length}
                      </button>
                    </td>
                    <td style={{ padding: "12px 16px", color: "#065f46", fontWeight: 700 }}>{active}</td>
                    <td style={{ padding: "12px 16px" }}>
                      <button onClick={() => { navigator.clipboard?.writeText(signupUrl); showToast("URL copied"); }} style={{ background: "none", border: "1px solid #ddd", borderRadius: 6, padding: "4px 10px", fontSize: 12, cursor: "pointer", color: "#003353", fontWeight: 700 }}>
                        Copy URL
                      </button>
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      <span style={{ background: c.status === "active" ? "#ecfdf5" : "#f9fafb", color: c.status === "active" ? "#065f46" : "#6b7280", fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 999 }}>
                        {c.status}
                      </span>
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      <button onClick={() => toggleStatus(c)} style={{ padding: "5px 12px", borderRadius: 6, border: "none", background: c.status === "active" ? "#fee2e2" : "#ecfdf5", color: c.status === "active" ? "#991b1b" : "#065f46", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
                        {c.status === "active" ? "Deactivate" : "Activate"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Users for selected code */}
        {selectedCode && (
          <div style={{ background: "#fff", border: "1px solid #e8ebf3", borderRadius: 16, overflow: "hidden" }}>
            <div style={{ padding: "16px 20px", borderBottom: "1px solid #e8ebf3", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3 style={{ margin: 0, color: "#650202" }}>Users with code: {selectedCode}</h3>
              <button onClick={() => setSelectedCode(null)} style={{ background: "none", border: "none", color: "#657899", cursor: "pointer", fontSize: 14 }}>Close ✕</button>
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr style={{ background: "#f6f7fb" }}>
                  {["Email", "Name", "Plan", "Status", "Signed Up"].map(h => (
                    <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700, color: "#650202" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {selectedUsers.length === 0 ? (
                  <tr><td colSpan={5} style={{ padding: 24, textAlign: "center", color: "#657899" }}>No users with this code yet.</td></tr>
                ) : selectedUsers.map((u, i) => (
                  <tr key={u.uid} style={{ borderTop: "1px solid #e8ebf3", background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                    <td style={{ padding: "12px 16px", color: "#003353" }}>{u.email}</td>
                    <td style={{ padding: "12px 16px" }}>{u.name || u.displayName || "—"}</td>
                    <td style={{ padding: "12px 16px" }}>
                      <span style={{ background: "#f0f9ff", color: "#0369a1", fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 999 }}>
                        {u.planKey || u.subscriptionStatus || "free"}
                      </span>
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      <span style={{ background: u.subscriptionStatus === "active" ? "#ecfdf5" : "#f9fafb", color: u.subscriptionStatus === "active" ? "#065f46" : "#6b7280", fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 999 }}>
                        {u.subscriptionStatus || "free"}
                      </span>
                    </td>
                    <td style={{ padding: "12px 16px", color: "#657899", fontSize: 13 }}>
                      {u.createdAtMs ? new Date(u.createdAtMs).toLocaleDateString("en-IE") : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <style>{`
        .ad *, .ad *::before, .ad *::after { box-sizing: border-box; }
        .ad-wrap{max-width:1200px;margin:24px auto;padding:0 16px;width:100%}
      `}</style>
    </div>
  );
}
